import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ShieldCheck, ArrowRight, Snowflake, Timer, Package, BadgePercent } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { lenisStore } from "@/lib/lenisStore";

export default function Aparatos() {
  useEffect(() => {
    lenisStore.current?.scrollTo(0, { immediate: true });
  }, []);

  return (
    <div data-testid="aparatos-page">
      <Navbar />
      <main className="bg-ocean-50/50 pb-20 pt-32 lg:pt-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-navy lg:grid lg:grid-cols-2">
            <div className="grain-overlay pointer-events-none absolute inset-0" />
            <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-ocean-600/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-ember/20 blur-3xl" />

            <div className="relative p-8 sm:p-12 lg:p-16">
              <motion.span
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                data-testid="aparatos-flash-badge"
                className="inline-flex items-center gap-2 rounded-full bg-ember px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white shadow-lg shadow-ember/30"
              >
                <Zap className="h-3.5 w-3.5" />
                Promoción Flash
              </motion.span>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl text-balance"
              >
                Split Kosner en promoción flash
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-5 max-w-lg text-base leading-relaxed text-slate-300"
              >
                Máquina split de la marca Kosner disponible en cualquier tipo de
                frigorías, instalada por Clima Oliver con
                <strong className="text-white"> 3 años de garantía</strong> incluidos.
                Oferta válida <strong className="text-white">por tiempo limitado</strong> en
                la provincia de Tarragona y Barcelona.
              </motion.p>

              <motion.ul
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="mt-8 space-y-4"
              >
                {[
                  { icon: Snowflake, text: "Disponible en cualquier tipo de frigorías" },
                  { icon: ShieldCheck, text: "3 años de garantía en la máquina incluidos" },
                  { icon: Timer, text: "Promoción flash por tiempo limitado" },
                ].map((item) => (
                  <li key={item.text} className="flex items-center gap-3 text-sm text-slate-200" data-testid={`aparatos-benefit-${item.text.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}>
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-ocean-500">
                      <item.icon className="h-4 w-4" />
                    </span>
                    {item.text}
                  </li>
                ))}
              </motion.ul>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/presupuesto"
                  data-testid="aparatos-quote-btn"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-ember px-7 py-3.5 text-sm font-semibold text-white shadow-xl shadow-ember/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-ember-700"
                >
                  Crear mi presupuesto ahora
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <p className="text-xs text-slate-400">
                  Indique «Split Kosner» en su solicitud para acogerse a la promoción.
                </p>
              </motion.div>
            </div>

            <Reveal className="relative flex items-center justify-center p-8 sm:p-12" delay={0.2}>
              <div className="relative w-full max-w-md">
                <div className="overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-white/20">
                  <img
                    src="/assets/kosner-split.jpg"
                    alt="Máquina split de aire acondicionado Kosner en promoción flash"
                    className="aspect-[4/3] w-full object-cover"
                    data-testid="aparatos-kosner-image"
                  />
                </div>
                <span className="absolute -right-3 -top-3 rotate-3 rounded-xl bg-ember px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-white shadow-xl shadow-ember/40" data-testid="aparatos-limited-badge">
                  Tiempo limitado
                </span>
              </div>
            </Reveal>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2" data-testid="aparatos-extras">
            <Reveal>
              <article className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-ocean-600/30 hover:shadow-xl sm:p-10" data-testid="aparatos-stock-card">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ocean-50 text-ocean-600">
                  <Package className="h-6 w-6" />
                </span>
                <h2 className="mt-6 font-display text-xl font-bold text-navy sm:text-2xl">
                  Stock de cualquier tipo de máquina
                </h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">
                  Disponemos de stock permanente de cualquier tipo de máquina: split 1x1,
                  multi-split, conductos, cassette, suelo/techo, fancoil, VRV, VRF y
                  monobloque. Sin esperas de proveedor: elegimos el equipo, lo instalamos
                  y usted empieza a disfrutarlo cuanto antes.
                </p>
                <Link
                  to="/presupuesto"
                  data-testid="aparatos-stock-quote-link"
                  className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ocean-700 transition-colors hover:text-ember"
                >
                  Consultar disponibilidad
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </article>
            </Reveal>

            <Reveal delay={0.12}>
              <article className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-ember/40 hover:shadow-xl sm:p-10" data-testid="aparatos-mitsubishi-card">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ember/10 text-ember">
                  <BadgePercent className="h-6 w-6" />
                </span>
                <h2 className="mt-6 font-display text-xl font-bold text-navy sm:text-2xl">
                  Promociones en Mitsubishi Electric
                </h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">
                  Tenemos promociones activas en equipos Mitsubishi Electric, una de las
                  marcas más fiables y eficientes del mercado. Consúltenos la oferta
                  vigente para su hogar o negocio y la incluiremos en su presupuesto.
                </p>
                <Link
                  to="/presupuesto"
                  data-testid="aparatos-mitsubishi-quote-link"
                  className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ocean-700 transition-colors hover:text-ember"
                >
                  Pedir oferta Mitsubishi Electric
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </article>
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
